/** The charter's written sections, with a Generate button on each.
 *
 * Brian's question was whether the Copilot summary should become the charter's opening
 * line, or whether each section should have its own Generate button. Per-section won: a
 * single summary paragraph is the one part of a charter nobody needs help with, while
 * "what is the business case" is the part that stalls for a week.
 *
 * Three rules this screen exists to enforce:
 *
 * 1. **A draft is labelled until a person edits it.** The pill is not decoration — an
 *    unreviewed paragraph and a considered one must not look identical, here or in the
 *    generated document.
 * 2. **Generating never silently destroys typing.** It replaces the stored text, so if
 *    there are unsaved edits it asks first.
 * 3. **The button is hidden, not broken, when drafting is unavailable.** A button that
 *    fails when pressed teaches people the feature is unreliable.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, Maximize2, Sparkles, Undo2, X } from 'lucide-react'

import { Button, Can, ErrorState, Loading, Pill, SectionHeader } from '@/components/ui'
import { Permissions, usePermission } from '@/lib/session'
import {
  useCharterSections,
  useDraftSection,
  useSaveSection,
  type CharterSection,
} from '@/features/documents/useDocuments'

const textareaClass =
  'mt-2 w-full resize-y rounded-md border border-border bg-surface px-3 py-2 text-sm text-fg ' +
  'placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-[var(--brand)]'

/** The box grows with its text, up to a cap, so a long section is read rather than
 * scrolled four lines at a time. Past the cap it scrolls, and Expand is there for that. */
const INLINE_MAX_PX = 420

function useAutoGrow(value: string) {
  const ref = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    const box = ref.current
    if (!box) return
    box.style.height = 'auto'
    box.style.height = `${Math.min(box.scrollHeight + 2, INLINE_MAX_PX)}px`
  }, [value])
  return ref
}

/**
 * One section, full screen.
 *
 * The same text as the box it came from — not a copy — so typing here is typing there,
 * and Save means the same thing in both places. Read-only for anyone who cannot edit.
 *
 * Escape is caught in the capture phase and stopped: the project drawer underneath closes
 * on Escape too, and backing out of a reading view must not also throw away the drawer.
 */
function ExpandedSection({
  label,
  text,
  onChange,
  canEdit,
  dirty,
  busy,
  onSave,
  onClose,
}: {
  label: string
  text: string
  onChange: (value: string) => void
  canEdit: boolean
  dirty: boolean
  busy: boolean
  onSave: () => void
  onClose: () => void
}) {
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      onClose()
    }
    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="flex h-[88vh] w-full max-w-4xl flex-col rounded-lg bg-canvas p-6 shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={label}
      >
        <div className="mb-3 flex items-center gap-3">
          <h2 className="font-heading text-2xl text-fg">{label}</h2>
          <span className="flex-1" />
          {canEdit ? (
            <Button variant="primary" onClick={onSave} disabled={!dirty || busy}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-fg-muted hover:bg-surface"
            aria-label="Close"
            title={dirty ? 'Close — your edits stay in the box, unsaved' : 'Close'}
          >
            <X size={18} aria-hidden />
          </button>
        </div>
        {canEdit ? (
          <textarea
            className={`${textareaClass} mt-0 flex-1 resize-none text-[15px] leading-relaxed`}
            value={text}
            autoFocus
            onChange={(event) => onChange(event.target.value)}
            aria-label={label}
          />
        ) : (
          <div className="flex-1 overflow-auto whitespace-pre-wrap text-[15px] leading-relaxed text-fg">
            {text || <span className="text-fg-muted">Nothing written yet.</span>}
          </div>
        )}
        {canEdit && dirty ? (
          <p className="mt-2 text-xs text-fg-muted">Unsaved — Save here or in the drawer.</p>
        ) : null}
      </div>
    </div>
  )
}

function SectionEditor({
  projectId,
  section,
  draftingAvailable,
}: {
  projectId: string
  section: CharterSection
  draftingAvailable: boolean
}) {
  const draft = useDraftSection(projectId)
  const save = useSaveSection(projectId)

  const [text, setText] = useState(section.content)
  // What the server last told us this section says. Used to decide whether the box holds
  // unsaved work, and to adopt regenerated text without clobbering someone mid-sentence.
  const serverText = useRef(section.content)

  useEffect(() => {
    if (section.content !== serverText.current) {
      serverText.current = section.content
      setText(section.content)
    }
  }, [section.content])

  const dirty = text !== serverText.current
  const busy = draft.isPending || save.isPending
  const canEdit = usePermission(Permissions.documentsManage)
  const [expanded, setExpanded] = useState(false)
  const boxRef = useAutoGrow(text)

  const onSave = () =>
    save.mutate({
      sectionKey: section.sectionKey,
      content: text,
      rowVersion: section.rowVersion,
    })

  const onGenerate = () => {
    // Generating replaces the stored text. Asking is cheap; retyping a paragraph is not.
    if (dirty && !window.confirm('Replace what you have typed with a fresh draft?')) return
    draft.mutate(section.sectionKey)
  }

  return (
    <li className="px-4 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-fg">{section.label}</span>
        {section.isAiDraft ? (
          <Pill tone="amber">AI draft — not yet reviewed</Pill>
        ) : section.content ? (
          <Pill tone="green">Written</Pill>
        ) : (
          <Pill>Empty</Pill>
        )}
        {/* For readers as much as writers: a long business case in a drawer is a lot of
            scrolling in a narrow column. */}
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs text-fg-muted hover:bg-surface hover:text-fg"
          title="Open this section full screen"
        >
          <Maximize2 size={12} aria-hidden />
          Expand
        </button>
        <span className="flex-1" />
        <Can permission={Permissions.documentsManage}>
          <div className="flex items-center gap-2">
            {dirty ? (
              <Button
                variant="secondary"
                onClick={() => setText(serverText.current)}
                disabled={busy}
              >
                <Undo2 size={14} aria-hidden />
                Discard
              </Button>
            ) : null}
            {/* Accepting a draft unchanged is the likeliest outcome of reviewing a good
                one, and until now there was no way to say so: the review flag only cleared
                when the text changed, so Save sat disabled and the amber "not yet reviewed"
                badge stayed on work that had been read and approved. It saves the text
                exactly as it stands — the server marks any save as human-authored, so no
                new endpoint is needed. Offered only while the badge is actually showing. */}
            {section.isAiDraft && !dirty ? (
              <Button
                variant="primary"
                disabled={busy}
                title="Keep this wording and mark it reviewed."
                onClick={() =>
                  save.mutate({
                    sectionKey: section.sectionKey,
                    content: text,
                    rowVersion: section.rowVersion,
                  })
                }
              >
                <Check size={14} aria-hidden />
                {save.isPending ? 'Accepting…' : 'Accept as written'}
              </Button>
            ) : null}
            <Button variant="secondary" onClick={onSave} disabled={!dirty || busy}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
            {draftingAvailable ? (
              <Button variant="primary" onClick={onGenerate} disabled={busy}>
                <Sparkles size={14} aria-hidden />
                {draft.isPending ? 'Drafting…' : section.content ? 'Redraft' : 'Generate'}
              </Button>
            ) : null}
          </div>
        </Can>
      </div>

      <Can
        permission={Permissions.documentsManage}
        fallback={
          <p className="mt-2 whitespace-pre-wrap text-sm text-fg">
            {section.content || <span className="text-fg-muted">Nothing written yet.</span>}
          </p>
        }
      >
        <textarea
          ref={boxRef}
          className={textareaClass}
          rows={5}
          value={text}
          onChange={(event) => setText(event.target.value)}
          disabled={busy}
          aria-label={section.label}
          placeholder={
            draftingAvailable
              ? 'Write this section, or press Generate to draft it from the project.'
              : 'Write this section.'
          }
        />
      </Can>

      <p className="mt-1.5 text-xs text-fg-muted">
        {section.isAiDraft && section.generatedAt
          ? `Drafted ${new Date(section.generatedAt).toLocaleString()}` +
            (section.generatedModel ? ` by ${section.generatedModel}` : '') +
            '. Edit and save it, or accept it as written, to mark it reviewed.'
          : section.modifiedBy
            ? `Last edited by ${section.modifiedBy} on ${new Date(
                section.modifiedDate,
              ).toLocaleDateString()}`
            : 'Not written yet.'}
      </p>

      {draft.error ? <ErrorState error={draft.error} /> : null}
      {save.error ? <ErrorState error={save.error} /> : null}

      {expanded ? (
        <ExpandedSection
          label={section.label}
          text={text}
          onChange={setText}
          canEdit={canEdit}
          dirty={dirty}
          busy={busy}
          onSave={onSave}
          onClose={() => setExpanded(false)}
        />
      ) : null}
    </li>
  )
}

export function CharterSections({ projectId }: { projectId: string }) {
  const { data, isPending, error } = useCharterSections(projectId)

  const written = useMemo(
    () => (data?.sections ?? []).filter((section) => section.content.trim()).length,
    [data?.sections],
  )

  if (isPending) {
    return (
      <section>
        <SectionHeader>Charter sections</SectionHeader>
        <Loading label="Loading sections" />
      </section>
    )
  }

  if (error) {
    return (
      <section>
        <SectionHeader>Charter sections</SectionHeader>
        <ErrorState error={error} />
      </section>
    )
  }

  const sections = data?.sections ?? []
  const usage = data?.usage

  return (
    <section>
      <SectionHeader>
        Charter sections
        <span className="ml-2 text-xs font-normal text-fg-muted">
          {written} of {sections.length} written
        </span>
      </SectionHeader>

      <ul className="card divide-y divide-border">
        {sections.map((section) => (
          <SectionEditor
            key={section.sectionKey}
            projectId={projectId}
            section={section}
            draftingAvailable={data?.draftingAvailable ?? false}
          />
        ))}
      </ul>

      <p className="mt-2 text-xs text-fg-muted">
        {data?.draftingAvailable ? (
          <>
            These sections go into the generated Project Charter. Drafts are written from this
            project&rsquo;s own data — its stage, dates, milestones, work items and dependencies
            — and are marked as drafts until someone edits them. Read one before you send it:
            the model can only work from what the project actually records.
          </>
        ) : (
          <>
            These sections go into the generated Project Charter. Drafting is unavailable
            because no language-model key is configured, so sections are written by hand.
          </>
        )}
      </p>

      {usage && data?.draftingAvailable ? (
        <p className="mt-1 text-xs text-fg-muted">
          {usage.monthToDateTokens.toLocaleString()} of {usage.monthlyTokenCap.toLocaleString()}{' '}
          tokens used this month.
        </p>
      ) : null}
    </section>
  )
}
